import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    autocompletePropertyWrapper: InjectableCss;
    ItemPropertyFont: InjectableCss;
    ItemPropertyWrapper: InjectableCss;
    stockWrapper: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        css: css`
            text-align: left;
            margin: .625rem 0 .31250625rem 0;
            cursor: default;
            color: #777;
            padding: .31250625rem 0;
            font-size: 1.0666666667rem;
            line-height: 1.25;
            font-weight: 400;
            display: none;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: .31250625rem 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: .31250625rem 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 4.0625rem;
            max-height: 4.0625rem;
            flex-basis: 4.0625rem;
        `,
    },
    image: {
        width: "3.125rem",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 16.875rem;
            > span {
                color: #002554 !important;
                font-size: 1rem;
                font-family: 'Roboto', sans-serif;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            color: #777;
            width: 16.875rem;
            text-align: left;
            margin: .31250625rem 0 0 0;
        `,
    },
    autocompletePropertyWrapper: {
        css: css`
            color: #777;
            width: 16.875rem;
            text-align: left;
            margin: .31250625rem 0 0 0;
            margin-right: .625rem;
        `,
    },
    ItemPropertyFont: {
        css: css`
            font-size: .6875rem;
        `,
    },
    ItemPropertyWrapper: {
        css: css`
            margin-right: .625rem;
        `,
    },
    stockWrapper: {},
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <Typography {...styles.headerText}>{translate("Products")}</Typography>
                {products.map((product, index) => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={index}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                    >
                        <GridItem {...styles.imageGridItem}>
                            <LazyImage {...styles.image} src={product.image} />
                        </GridItem>
                        <GridItem {...styles.infoGridItem}>
                            <Link {...styles.titleLink}>{product.displayTitle}</Link>
                            <StyledWrapper {...styles.autocompletePropertyWrapper}>
                                <Typography {...styles.ItemPropertyWrapper}>
                                    <Typography {...styles.ItemPropertyFont}>{translate("MFG #")}</Typography>
                                    <Typography>{product.manufacturerItemNumber}</Typography>
                                </Typography>

                                <Typography {...styles.ItemPropertyWrapper}>
                                    <Typography {...styles.ItemPropertyFont}>{translate("In Stock :")}</Typography>
                                    <Typography>{product.properties.isStocked}</Typography>
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
